import { useState } from 'react';
import { TextInput } from '@pmi/dsm-react';
import { Option } from '@pmi/dsm-react/dist/components/menus/types';
import Overlay from '../../../../../shared/components/Overlay';
import { useFormikContext } from 'formik';
import { Form } from 'react-bootstrap';
import { useStatesQueryMutation } from '../apiSlice';
import { ProfileModel, SelectOption } from './models';
import styles from '../productModal.module.scss';

export default function AddressForm({
  countries,
  onCountryRestricted = (isRestricted: boolean = false) => isRestricted,
}: {
  countries: SelectOption[];
  onCountryRestricted: (isRestricted: boolean) => void;
}) {
  const formik = useFormikContext<ProfileModel>();
  const [statesQuery, { reset: resetStates, isLoading: statesLoading }] =
    useStatesQueryMutation();
  const countryRegionOptions: Option[] =
    countries?.map((c) => ({
      label: c.text,
      value: c.value,
    })) ?? [];

  const [countryOptions, setCountryOptions] = useState(countryRegionOptions);
  const [stateOptions, setStateOptions] = useState([]);
  const onCountryRegionChange = async (value: string) => {
    formik.setFieldTouched('address.state', true);
    if (value) {
      resetStates();
      const statesResponse = await statesQuery({
        country: value,
      }).unwrap();
      const stateOptionsFromResponse = statesResponse?.map((opt) => ({
        label: opt.text,
        value: opt.value,
      }));
      formik.setFieldValue('stateProvinceOptions', stateOptionsFromResponse);
      setStateOptions(stateOptionsFromResponse);
    } else {
      formik.setFieldValue('stateProvince', null);
      setStateOptions([]);
    }
  };

  const handleCountryDropdownChange = async (e: any, value: string) => {
    formik.setFieldTouched('address.country', true);
    if (value && value.toLocaleLowerCase() === 'chn') {
      onCountryRestricted(true);
      return;
    }
    onCountryRestricted(false);
    formik.setFieldValue('address.country', value);
    setCountryOptions(countryRegionOptions);
    await onCountryRegionChange(value);
  };

  return (
    <>
      <h4>Address</h4>
      <div className="row">
        <div className="col-12 mb-3 mt-2">
          <span className="text-danger">*</span> These fields are required.
        </div>
        <div className="col-12 mb-3">
          <Form.Group
            controlId="address.country"
            className={formik?.errors.address?.country ? 'is-invalid' : null}
          >
            <Form.Label>
              Country/Region <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              aria-label="Country/Region"
              className={`dsm-input-field__form-control cursor-pointer ${
                formik?.errors.address?.country
                  ? 'dsm-input-field__form-control--error'
                  : ''
              }`}
              name="address.country"
              onChange={(e) => handleCountryDropdownChange(e, e.target.value)}
              value={formik?.values.address?.country}
            >
              <option key="" value="" className="dsm-menu__label">
                Select
              </option>
              {countryOptions.map((option) => (
                <option
                  className="dsm-menu__label break-all-words mw-100"
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {formik?.errors.address?.country ? (
              <div className="invalid-feedback">Country is required.</div>
            ) : null}
          </Form.Group>
        </div>
        <div className="col-12 mb-3">
          <Form.Group
            controlId="address.address1"
            className={formik?.errors.address?.address1 ? 'is-invalid' : null}
          >
            <Form.Label>
              Address Line 1 <span className="text-danger">*</span>
            </Form.Label>
            <TextInput
              name="address.address1"
              onChange={formik?.handleChange}
              isInvalid={!!formik?.errors.address?.address1}
              className={
                formik?.errors.address?.address1
                  ? 'is-invalid'
                  : styles.inputBorder
              }
              validationMessage="Address line 1 is required."
              value={formik.values.address?.address1}
            />
          </Form.Group>
        </div>
        <div className="col-12 mb-3">
          <Form.Group
            controlId="address.address2"
            className={formik.errors.address?.address2 ? 'is-invalid' : null}
          >
            <Form.Label>Address Line 2</Form.Label>
            <TextInput
              name="address.address2"
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.address?.address2}
              className={
                formik.errors.address?.address2
                  ? 'is-invalid'
                  : styles.inputBorder
              }
              validationMessage={formik.errors.address?.address2}
              value={formik.values.address?.address2}
            />
          </Form.Group>
        </div>
        <div className="col-12 mb-3">
          <Form.Group
            controlId="address.postalCode"
            className={formik.errors.address?.postalCode ? 'is-invalid' : null}
          >
            <Form.Label>
              Postal Code <span className="text-danger">*</span>
            </Form.Label>
            <TextInput
              name="address.postalCode"
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.address?.postalCode}
              className={
                formik.errors.address?.postalCode
                  ? 'is-invalid'
                  : styles.inputBorder
              }
              validationMessage={`Postal code is ${
                formik.values?.address?.postalCode?.length > 0
                  ? 'incorrect'
                  : 'required'
              }.`}
              value={formik.values.address?.postalCode}
            />
          </Form.Group>
        </div>
      </div>
      <div className="row">
        <div className="col-6 mb-3" data-testid="address_city_container_testid">
          <Form.Group
            controlId="address.city"
            className={formik.errors.address?.city ? 'is-invalid' : null}
          >
            <Form.Label>
              City <span className="text-danger">*</span>
            </Form.Label>
            <TextInput
              name="address.city"
              onChange={formik.handleChange}
              isInvalid={!!formik.errors.address?.city}
              className={
                formik.errors.address?.city ? 'is-invalid' : styles.inputBorder
              }
              validationMessage="City is required."
              value={formik.values.address?.city}
            />
          </Form.Group>
        </div>
        <div className="col-6 mb-3">
          <Form.Group
            controlId="address.state"
            className={`${formik.errors.address?.state ? 'is-invalid' : null} ${
              formik.values?.address?.country ? null : 'cursor-not-allowed'
            }`}
          >
            <Form.Label>
              State/Province <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              aria-label="State/Province"
              className={`dsm-input-field__form-control cursor-pointer ${
                formik.errors.address?.state
                  ? 'dsm-input-field__form-control--error'
                  : ''
              }
              ${
                formik.values?.address?.country ? null : 'pointer-events-none'
              }`}
              name="address.state"
              onChange={(e) => {
                formik.setFieldValue('address.state', e.target.value);
                setStateOptions(formik.values.stateProvinceOptions);
              }}
              value={formik.values.address?.state}
            >
              <option key="" value="" className="dsm-menu__label">
                Select
              </option>
              {stateOptions?.map((option) => (
                <option
                  className="dsm-menu__label"
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </Form.Select>
            {formik.errors.address?.state ? (
              <div className="invalid-feedback">
                State/province is required.
              </div>
            ) : null}
          </Form.Group>
        </div>
      </div>

      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={statesLoading}
      />
    </>
  );
}
